/**
 * AIBillingSetupWizard — Wizard IA « Setup Ventes en un clic ».
 *
 * L'utilisateur dépose sa dernière facture (± devis, ± avoir). Le backend
 * extrait via LLM le schéma de numérotation ET les paramètres Ventes
 * (IBAN, délai paiement, mentions légales, TVA). L'utilisateur voit un
 * récap clair et confirme — la numérotation est alors VERROUILLÉE pour
 * conformité fiscale FR (art. 242 nonies A CGI).
 *
 * 3 étapes : upload → preview → confirmation.
 */
import { useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Sparkles, Upload, FileText, CheckCircle2, Lock, Loader2,
  AlertTriangle, ArrowLeft, ArrowRight, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const STEPS = ["upload", "preview", "done"];

function FileDrop({ label, file, onPick, required, testid }) {
  const inputRef = useRef(null);
  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) onPick(f);
  };
  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      className={cn(
        "relative border-2 border-dashed rounded-xl p-4 cursor-pointer transition",
        "hover:border-blue-400 hover:bg-blue-50/40",
        file ? "border-emerald-400 bg-emerald-50/40" : "border-slate-300 bg-slate-50/40",
      )}
      data-testid={testid}
    >
      <input
        id={testid ? `${testid}-file` : "ai-billing-file"}
        name={testid ? `${testid}-file` : "ai-billing-file"}
        ref={inputRef}
        type="file"
        className="hidden"
        accept="application/pdf,image/png,image/jpeg,image/webp"
        onChange={(e) => e.target.files?.[0] && onPick(e.target.files[0])}
      />
      <div className="flex items-center gap-3">
        {file ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
        ) : (
          <Upload className="w-5 h-5 text-slate-400 flex-shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-800">
            {label}
            {required && <span className="text-red-600 ml-1">*</span>}
          </p>
          <p className="text-xs text-slate-500 truncate">
            {file ? file.name : "Cliquez ou glissez un PDF / JPG / PNG"}
          </p>
        </div>
        {file && (
          <Button
            type="button" size="sm" variant="ghost"
            onClick={(e) => { e.stopPropagation(); onPick(null); }}
            className="text-slate-500 hover:text-red-600">
            <XCircle className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

function DetectionRow({ icon: Icon, label, value, sub, missing }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-0">
      <div className={cn(
        "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
        missing ? "bg-slate-100" : "bg-emerald-50",
      )}>
        <Icon className={cn(
          "w-4 h-4", missing ? "text-slate-400" : "text-emerald-600",
        )} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
          {label}
        </p>
        {value ? (
          <p className="text-sm font-medium text-slate-900 truncate">{value}</p>
        ) : (
          <p className="text-sm text-slate-400 italic">— non détecté —</p>
        )}
        {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

export default function AIBillingSetupWizard({ open, onOpenChange, onDone, mode = "full" }) {
  // mode : "full"      → 3 zones (facture obligatoire, devis/avoir optionnels) — 1er setup
  //        "add_quote"  → 1 zone devis seulement — ajout post-verrouillage
  //        "add_credit" → 1 zone avoir seulement — ajout post-verrouillage
  const [step, setStep] = useState("upload");
  const [files, setFiles] = useState({ invoice: null, quote: null, credit_note: null });
  const [detecting, setDetecting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setStep("upload"); setFiles({ invoice: null, quote: null, credit_note: null });
    setResult(null); setDetecting(false); setApplying(false);
  };

  const runDetection = async () => {
    const requiredKey = mode === "add_quote" ? "quote"
                       : mode === "add_credit" ? "credit_note"
                       : "invoice";
    if (!files[requiredKey]) {
      toast.error(mode === "full"
        ? "Merci de déposer au moins votre dernière facture."
        : mode === "add_quote"
          ? "Merci de déposer votre dernier devis."
          : "Merci de déposer votre dernier avoir.");
      return;
    }
    setDetecting(true);
    try {
      const form = new FormData();
      if (files.invoice) form.append("invoice", files.invoice);
      if (files.quote) form.append("quote", files.quote);
      if (files.credit_note) form.append("credit_note", files.credit_note);
      const r = await api.post("/billing/ai-detect-setup", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      setStep("preview");
    } catch (e) {
      toast.error(apiError(e, "Détection IA échouée"));
    } finally { setDetecting(false); }
  };

  const applyResult = async () => {
    if (!result) return;
    setApplying(true);
    try {
      const invNum = result.numbering?.invoice || {};
      const r = await api.post("/billing/ai-detect-setup/apply", {
        numbering: result.numbering,
        sales_defaults: result.sales_defaults,
        seller: result.sales_defaults?.seller || {},
        source_detected_at: result.detected_at,
        detected_invoice_number: invNum.detected_number,
        source_filename: files.invoice?.name
          || files.quote?.name
          || files.credit_note?.name
          || null,
      });
      toast.success(r.data?.additive
        ? "Format ajouté à ta configuration existante"
        : "Configuration appliquée · Numérotation verrouillée");
      setStep("done");
      onDone?.();
    } catch (e) {
      toast.error(apiError(e, "Application échouée"));
    } finally { setApplying(false); }
  };

  const close = () => {
    reset();
    onOpenChange?.(false);
  };

  const inv = result?.numbering?.invoice;
  const nextInvNumber = inv?.template && inv?.current_seq != null
    ? previewNext(inv.template, inv.current_seq + 1)
    : null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-2xl" data-testid="ai-billing-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="w-5 h-5 text-purple-600" />
            Configurer la facturation avec l'IA
          </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
          <StepPills current={step} />
        </DialogHeader>

        {step === "upload" && (
          <div className="space-y-4">
            <Alert className="bg-blue-50 border-blue-200">
              <Sparkles className="w-4 h-4 text-blue-600" />
              <AlertDescription className="text-xs text-blue-900">
                {mode === "full" && "Dépose ta dernière facture — notre IA détecte automatiquement ton format de numérotation, ton IBAN, tes délais de paiement et tes mentions légales. Aucune facture n'est stockée."}
                {mode === "add_quote" && "Dépose ton dernier devis — notre IA détecte le format de numérotation à utiliser pour tes futurs devis Averdy Agency. La numérotation de tes factures ne sera pas affectée."}
                {mode === "add_credit" && "Dépose ton dernier avoir — notre IA détecte le format de numérotation à utiliser pour tes futurs avoirs Averdy Agency. La numérotation de tes factures ne sera pas affectée."}
              </AlertDescription>
            </Alert>
            {mode === "full" && (
              <FileDrop
                label="Ta dernière facture"
                required
                file={files.invoice}
                onPick={(f) => setFiles((p) => ({ ...p, invoice: f }))}
                testid="drop-invoice"
              />
            )}
            {(mode === "full" || mode === "add_quote") && (
              <FileDrop
                label={mode === "add_quote" ? "Ton dernier devis" : "Ton dernier devis (optionnel)"}
                required={mode === "add_quote"}
                file={files.quote}
                onPick={(f) => setFiles((p) => ({ ...p, quote: f }))}
                testid="drop-quote"
              />
            )}
            {(mode === "full" || mode === "add_credit") && (
              <FileDrop
                label={mode === "add_credit" ? "Ton dernier avoir" : "Ton dernier avoir (optionnel)"}
                required={mode === "add_credit"}
                file={files.credit_note}
                onPick={(f) => setFiles((p) => ({ ...p, credit_note: f }))}
                testid="drop-credit"
              />
            )}
          </div>
        )}

        {step === "preview" && result && (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {inv?.detected_number ? (
              <Card className="border-2 border-emerald-400 bg-gradient-to-br from-emerald-50 via-white to-blue-50 shadow-md">
                <CardContent className="pt-5 pb-5">
                  <p className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold mb-3">
                    ✨ Numérotation détectée par l'IA
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-4">
                    <div className="text-center md:text-left">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
                        Facture importée
                      </p>
                      <p className="font-mono text-xl font-bold text-slate-900 mt-1"
                          data-testid="detected-source-number">
                        {inv.detected_number}
                      </p>
                    </div>
                    <ArrowRight className="w-6 h-6 text-emerald-500 mx-auto hidden md:block" />
                    <div className="text-center md:text-right">
                      <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-medium">
                        Prochaine facture Averdy Agency
                      </p>
                      <p className="font-mono text-xl font-bold text-emerald-700 mt-1"
                          data-testid="next-invoice-number">
                        {nextInvNumber || "—"}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-emerald-200 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-600">
                    <span>
                      Format détecté :{" "}
                      <code className="font-mono bg-white/60 px-1.5 py-0.5 rounded text-slate-800">
                        {inv.template}
                      </code>
                    </span>
                    <span>
                      Confiance IA : <span className="font-semibold">
                        {Math.round((inv.confidence || 0) * 100)}%
                      </span>
                    </span>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <AlertDescription className="text-xs text-amber-900">
                  Impossible de détecter le format de numérotation. Utilise le
                  mode expert pour le configurer manuellement.
                </AlertDescription>
              </Alert>
            )}

            <Card>
              <CardContent className="pt-4 pb-2">
                <p className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-2">
                  Paramètres Ventes extraits
                </p>
                <DetectionRow
                  icon={FileText}
                  label="Délai de paiement"
                  value={result.sales_defaults?.default_payment_terms_days != null
                    ? `${result.sales_defaults.default_payment_terms_days} jours`
                    : null}
                  missing={result.sales_defaults?.default_payment_terms_days == null}
                />
                <DetectionRow
                  icon={FileText}
                  label="IBAN"
                  value={result.sales_defaults?.bank_accounts?.[0]?.iban}
                  sub={result.sales_defaults?.bank_accounts?.[0]?.bank_name}
                  missing={!result.sales_defaults?.bank_accounts?.[0]?.iban}
                />
                <DetectionRow
                  icon={FileText}
                  label="Pénalités de retard"
                  value={result.sales_defaults?.default_late_penalty_text}
                  missing={!result.sales_defaults?.default_late_penalty_text}
                />
                <DetectionRow
                  icon={FileText}
                  label="Taux de TVA détectés"
                  value={(result.sales_defaults?.detected_vat_rates || []).length
                    ? result.sales_defaults.detected_vat_rates.join(" %, ") + " %"
                    : null}
                  missing={!result.sales_defaults?.detected_vat_rates?.length}
                />
                <DetectionRow
                  icon={FileText}
                  label="Mentions légales (footer)"
                  value={result.sales_defaults?.footer_text}
                  missing={!result.sales_defaults?.footer_text}
                />
                <DetectionRow
                  icon={FileText}
                  label="SIRET détecté"
                  value={result.sales_defaults?.seller?.siret}
                  sub={result.sales_defaults?.seller?.vat_number}
                  missing={!result.sales_defaults?.seller?.siret}
                />
              </CardContent>
            </Card>

            <Alert className="bg-amber-50 border-amber-200">
              <Lock className="w-4 h-4 text-amber-700" />
              <AlertDescription className="text-xs text-amber-900">
                <strong>Attention</strong> — une fois validée, la numérotation
                sera <strong>verrouillée</strong> pour conformité fiscale
                française. Toute modification ultérieure nécessitera une
                justification auditable.
              </AlertDescription>
            </Alert>
          </div>
        )}

        {step === "done" && (
          <div className="py-8 text-center space-y-3">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <p className="text-lg font-semibold text-slate-900">
              Facturation configurée
            </p>
            <p className="text-sm text-slate-600">
              Ta prochaine facture Averdy Agency sera{" "}
              <span className="font-mono font-bold">{nextInvNumber}</span>.
            </p>
          </div>
        )}

        <DialogFooter className="pt-3 border-t border-slate-100">
          {step === "upload" && (
            <>
              <Button variant="ghost" onClick={close}>Annuler</Button>
              <Button
                onClick={runDetection}
                disabled={!files.invoice || detecting}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="wizard-detect-btn">
                {detecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                             : <Sparkles className="w-4 h-4 mr-2" />}
                Détecter avec l'IA
              </Button>
            </>
          )}
          {step === "preview" && (
            <>
              <Button variant="outline" onClick={() => setStep("upload")}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Retour
              </Button>
              <Button
                onClick={applyResult}
                disabled={applying || !inv?.template}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="wizard-apply-btn">
                {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                             : <Lock className="w-4 h-4 mr-2" />}
                Valider et verrouiller
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}
          {step === "done" && (
            <Button onClick={close} className="w-full">Fermer</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StepPills({ current }) {
  return (
    <div className="flex items-center gap-2 mt-2">
      {STEPS.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <div className={cn(
            "w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold",
            STEPS.indexOf(current) >= i
              ? "bg-purple-600 text-white"
              : "bg-slate-200 text-slate-500",
          )}>
            {i + 1}
          </div>
          {i < STEPS.length - 1 && (
            <div className={cn(
              "h-0.5 w-8",
              STEPS.indexOf(current) > i ? "bg-purple-600" : "bg-slate-200",
            )} />
          )}
        </div>
      ))}
    </div>
  );
}

// Rendu client du prochain numéro (miroir minimal de numbering.py)
function previewNext(template, seq) {
  if (!template) return null;
  const now = new Date();
  const YYYY = String(now.getUTCFullYear());
  const YY = YYYY.slice(-2);
  const MM = String(now.getUTCMonth() + 1).padStart(2, "0");
  const DD = String(now.getUTCDate()).padStart(2, "0");
  return template.replace(/\{(YYYY|YY|MM|DD|SEQ)(?::(\d+))?\}/g, (_, tok, pad) => {
    if (tok === "YYYY") return YYYY;
    if (tok === "YY") return YY;
    if (tok === "MM") return MM;
    if (tok === "DD") return DD;
    if (tok === "SEQ") return String(seq).padStart(pad ? Number(pad) : 6, "0");
    return "";
  });
}
